/**
 * Application version and build metadata.
 * UI formatted version (`vX.Y.Z`) MUST match the GitHub Release / git tag name.
 */

export interface AppVersionInfo {
  version: string;
  buildTime: string;
  commitHash: string;
  formatted: string;
  releaseUrl: string;
  environment: 'development' | 'production' | 'test';
}

const DEFAULT_REPO_URL = 'https://github.com/judasane/zunelo';

function resolveBuildVersion(): string {
  if (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) {
    return __APP_VERSION__;
  }
  return '1.0.0';
}

function resolveBuildTimestamp(): string {
  if (typeof __BUILD_TIMESTAMP__ !== 'undefined' && __BUILD_TIMESTAMP__) {
    return __BUILD_TIMESTAMP__;
  }
  return '2026-08-15T00:00:00.000Z';
}

function resolveCommitHash(): string {
  if (typeof __COMMIT_HASH__ !== 'undefined' && __COMMIT_HASH__) {
    return __COMMIT_HASH__;
  }
  return 'dev';
}

function resolveEnvironment(): 'development' | 'production' | 'test' {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return 'test';
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE) {
    const mode = import.meta.env.MODE;
    if (mode === 'production' || mode === 'development' || mode === 'test') {
      return mode;
    }
  }
  return 'development';
}

function buildReleaseUrl(version: string): string {
  return `${DEFAULT_REPO_URL}/releases/tag/v${version}`;
}

const rawVersion = resolveBuildVersion();

export const APP_VERSION_INFO: AppVersionInfo = {
  version: rawVersion,
  buildTime: resolveBuildTimestamp(),
  commitHash: resolveCommitHash(),
  formatted: `v${rawVersion}`,
  releaseUrl: buildReleaseUrl(rawVersion),
  environment: resolveEnvironment(),
};

export function formatDisplayVersion(info: AppVersionInfo = APP_VERSION_INFO): string {
  return info.formatted;
}

export function formatFullBuildInfo(info: AppVersionInfo = APP_VERSION_INFO): string {
  return `Zunelo ${info.formatted} (${info.commitHash}) · Build ${info.buildTime} [${info.environment}] · ${info.releaseUrl}`;
}

export function initAppVersion(info: AppVersionInfo = APP_VERSION_INFO): void {
  if (typeof window === 'undefined') return;

  (window as unknown as { __ZUNELO_VERSION__: AppVersionInfo }).__ZUNELO_VERSION__ = info;

  console.info(
    `%c[Zunelo]%c ${info.formatted} (${info.commitHash}) — Built: ${info.buildTime} [${info.environment}]`,
    'font-weight: bold; color: #4f46e5;',
    'font-weight: normal; color: inherit;'
  );
}
